import React, { useState, useCallback } from "react";
import { RendererProps } from "./index";
import {
  ChoiceList,
  ChoiceOption,
  SelectWrapper,
  FieldLabel,
  DropdownReveal,
} from "./renderer-styles";

interface RowState {
  value: string;
  selectVal?: { value: string; title?: string };
}

function initRows(answer: RendererProps["answer"]): RowState[] {
  if (answer?.values && Array.isArray(answer.values)) {
    return answer.values.map((v: any) => ({
      value: v.value,
      selectVal: v.selectVal,
    }));
  }
  return [];
}

const ChoiceMultiDropdownRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {

  const [rows, setRows] = useState<RowState[]>(() => initRows(answer));

  const emit = useCallback(
    (nextRows: RowState[]) => {
      const isValid = nextRows.length > 0;
      onAnswer(
        null,
        isValid,
        nextRows.map((r) => ({
          value: r.value,
          selectVal: r.selectVal,
        }))
      );
    },
    [onAnswer]
  );

  const isChecked = useCallback(
    (val: string) => rows.some((r) => String(r.value) === String(val)),
    [rows]
  );

  const toggleChoice = useCallback(
    (val: string) => {
      setRows((prev) => {
        const exists = prev.find((r) => String(r.value) === String(val));
        const next = exists
          ? prev.filter((r) => String(r.value) !== String(val))
          : [...prev, { value: val }];
        emit(next);
        return next;
      });
    },
    [emit]
  );

  const setDropdownVal = useCallback(
    (choiceVal: string, selectedOptValue: string) => {
      setRows((prev) => {
        const opts = question.options || [];
        const opt = opts.find((o) => String(o.value) === selectedOptValue);
        const next = prev.map((r) =>
          String(r.value) === String(choiceVal)
            ? {
                ...r,
                selectVal: opt
                  ? { value: opt.value, title: opt.title || opt.label }
                  : undefined,
              }
            : r
        );
        emit(next);
        return next;
      });
    },
    [emit, question.options]
  );

  const choices = question.choices || [];
  const options = question.options || [];
  const dropdownLabel =
    question.dropdownLabel || question.dropdown_name || "Select";

  return (
    <ChoiceList>
      {choices.map((choice) => {
        const checked = isChecked(choice.value);
        const row = rows.find((r) => String(r.value) === String(choice.value));
        return (
          <div key={choice.value}>
            <ChoiceOption selected={checked}>
              <input
                type="checkbox"
                value={choice.value}
                checked={checked}
                onChange={() => toggleChoice(choice.value)}
              />
              {choice.title || choice.label}
            </ChoiceOption>
            <DropdownReveal $visible={checked}>
              <div style={{ paddingLeft: 30 }}>
                <FieldLabel>{dropdownLabel}</FieldLabel>
                <SelectWrapper
                  value={row?.selectVal?.value ?? ""}
                  onChange={(e) => setDropdownVal(choice.value, e.target.value)}
                  disabled={!checked}
                >
                  <option value="">—</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.title || opt.label}
                    </option>
                  ))}
                </SelectWrapper>
              </div>
            </DropdownReveal>
          </div>
        );
      })}
    </ChoiceList>
  );
};

export default ChoiceMultiDropdownRenderer;
